import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Slide
} from '@mui/material';
import {
  GetApp,
  Refresh,
  Close,
  CloudDownload,
  Update
} from '@mui/icons-material';

const PWAManager = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Handle PWA installation
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 PWA: Install prompt triggered');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show install prompt if not already installed
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        setShowInstallPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      console.log('✅ PWA: App installed successfully');
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 PWA: Back online');
      setIsOffline(false);
    };

    const handleOffline = () => {
      console.log('📴 PWA: Gone offline');
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);
        
        // Check for updates
        reg.addEventListener('updatefound', () => {
          console.log('🔄 PWA: Update found');
          const newWorker = reg.installing;
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('✅ PWA: Update ready');
              setUpdateAvailable(true);
              setShowUpdatePrompt(true);
            }
          });
        });
      });

      // Listen for service worker messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
          setUpdateAvailable(true);
          setShowUpdatePrompt(true);
        }
      });
    }
  }, []);

  // Install PWA
  const handleInstallClick = async () => {
    console.log('📱 PWA: Installing app...');
    
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log(`📱 PWA: Install ${outcome}`);
      
      if (outcome === 'accepted') {
        setShowInstallPrompt(false);
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('❌ PWA: Install failed', error);
    }
  };

  // Update PWA
  const handleUpdateClick = () => {
    console.log('🔄 PWA: Applying update...');
    
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Listen for service worker to take control
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('✅ PWA: Update applied, reloading...');
        window.location.reload();
      });
    }
    
    setShowUpdatePrompt(false);
  };

  // Dismiss prompts
  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    // Remember dismissal in localStorage to avoid showing again soon
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleDismissUpdate = () => {
    setShowUpdatePrompt(false);
  };

  // Check if install prompt should be shown
  const shouldShowInstallPrompt = () => {
    if (!showInstallPrompt || !deferredPrompt) return false;
    
    // Don't show if recently dismissed (within 7 days)
    const lastDismissed = localStorage.getItem('pwa-install-dismissed');
    if (lastDismissed) {
      const daysSinceLastDismissed = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24);
      if (daysSinceLastDismissed < 7) return false;
    }
    
    // Don't show if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return false;
    
    return true;
  };

  return (
    <>
      {/* Offline Indicator */}
      <Box
        className={`offline-indicator ${isOffline ? 'visible' : ''}`}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#f44336',
          color: 'white',
          textAlign: 'center',
          padding: 1,
          fontSize: '14px',
          zIndex: 1400,
          transform: isOffline ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease'
        }}
      >
        📴 Offline-Modus - Eingeschränkte Funktionalität
      </Box>

      {/* Install Prompt */}
      {shouldShowInstallPrompt() && (
        <Slide direction="up" in={true} mountOnEnter unmountOnExit>
          <Box
            className="pwa-install-prompt"
            sx={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#1976d2',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
              zIndex: 1300,
              maxWidth: 'calc(100vw - 40px)'
            }}
          >
            <GetApp />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Object-Link installieren
              </Typography>
              <Typography variant="caption">
                Schneller Zugriff vom Homescreen
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={handleInstallClick}
              sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Installieren
            </Button>
            <IconButton
              size="small"
              onClick={handleDismissInstall}
              sx={{ color: 'white', ml: 1 }}
            >
              <Close />
            </IconButton>
          </Box>
        </Slide>
      )}

      {/* Update Notification */}
      <Snackbar
        open={showUpdatePrompt}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: isOffline ? 6 : 2 }}
      >
        <Alert
          severity="info"
          variant="filled"
          sx={{
            background: '#4caf50',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' }
          }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={handleUpdateClick}
                sx={{
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                startIcon={<Refresh />}
              >
                Aktualisieren
              </Button>
              <IconButton
                size="small"
                onClick={handleDismissUpdate}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </Box>
          }
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Update verfügbar
            </Typography>
            <Typography variant="caption">
              Neue Version mit Verbesserungen
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Background Update Check */}
      {registration && (
        <Box sx={{ display: 'none' }}>
          {/* Hidden component that periodically checks for updates */}
          <UpdateChecker registration={registration} />
        </Box>
      )}
    </>
  );
};

// Component to periodically check for updates
const UpdateChecker = ({ registration }) => {
  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        await registration.update();
        console.log('🔍 PWA: Checked for updates');
      } catch (error) {
        console.log('🔍 PWA: Update check failed', error);
      }
    };

    // Check for updates every 60 seconds
    const interval = setInterval(checkForUpdates, 60000);

    // Initial check
    checkForUpdates();

    return () => clearInterval(interval);
  }, [registration]);

  return null;
};

export default PWAManager;